import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "./Reveal";

export const GalleryGrid = ({ images, filters = null }) => {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const reduce = useReducedMotion();

  const visible = filter === "All" ? images : images.filter((g) => g.category === filter);

  const move = useCallback(
    (dir) => setActive((a) => (a === null ? null : (a + dir + visible.length) % visible.length)),
    [visible.length]
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") move(1);
      if (e.key === "ArrowLeft") move(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, move]);

  return (
    <div>
      {filters && (
        <div className="flex flex-wrap gap-2 mb-10" role="group" aria-label="Filter gallery">
          {filters.map((f) => (
            <button
              key={f}
              type="button"
              data-testid={`gallery-filter-${f.toLowerCase()}`}
              onClick={() => { setFilter(f); setActive(null); }}
              aria-pressed={filter === f}
              className={`rounded-full px-5 py-2 text-sm font-medium font-display transition-all duration-300 border ${
                filter === f
                  ? "bg-ink text-ivory border-ink"
                  : "bg-transparent text-body border-ink/20 hover:border-copper hover:text-copper"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      )}

      <div className="columns-2 md:columns-3 gap-4 [column-fill:balance]" data-testid="gallery-grid">
        <AnimatePresence mode="popLayout">
          {visible.map((g, i) => (
            <motion.div
              key={g.src}
              layout={!reduce}
              initial={reduce ? false : { opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="mb-4 break-inside-avoid"
            >
              <Reveal delay={Math.min(i * 0.04, 0.3)} y={16}>
                <button
                  type="button"
                  data-testid={`gallery-item-${i}`}
                  onClick={() => setActive(i)}
                  aria-label={`Open image: ${g.alt}`}
                  className={`group relative block w-full overflow-hidden rounded-lg ${g.ratio} focus-visible:outline-copper`}
                >
                  <img
                    src={g.src}
                    alt={g.alt}
                    loading="lazy"
                    width={900}
                    height={900}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/25 transition-colors duration-500" aria-hidden="true" />
                  <span className="absolute bottom-3 left-3 rounded-full bg-ink/70 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-copper font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    {g.category}
                  </span>
                </button>
              </Reveal>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {active !== null && visible[active] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
            role="dialog"
            aria-modal="true"
            aria-label="Image lightbox"
            data-testid="gallery-lightbox"
            onClick={() => setActive(null)}
          >
            <button type="button" aria-label="Close lightbox" data-testid="lightbox-close" onClick={() => setActive(null)} className="absolute top-5 right-5 text-white/80 hover:text-copper p-2">
              <X size={28} />
            </button>
            <button type="button" aria-label="Previous image" data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); move(-1); }} className="absolute left-3 sm:left-8 text-white/80 hover:text-copper p-2">
              <ChevronLeft size={34} />
            </button>
            <motion.figure
              key={visible[active].src}
              initial={reduce ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl w-full"
            >
              <img src={visible[active].src.replace("w=900", "w=1600")} alt={visible[active].alt} className="w-full max-h-[78vh] object-contain rounded-lg" />
              <figcaption className="mt-4 text-center text-white/60 text-sm">{visible[active].alt}</figcaption>
            </motion.figure>
            <button type="button" aria-label="Next image" data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); move(1); }} className="absolute right-3 sm:right-8 text-white/80 hover:text-copper p-2">
              <ChevronRight size={34} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
